import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {DateTime} from 'luxon';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {
  ScheduleNodeOutputData,
  ScheduleType,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {scheduleOutputSchema} from '../../../../schemas/scheduleCardSchema';
import {ScheduleCardScratchpadState} from '../../scratchpad/scheduleCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import validateScheduleOutput from '../../utils/schedule/validateScheduleOutput';
import {UTCTimezone} from 'common/src/modules/timezones/timezones.consts';

const inputSchema = z.object({
  outputId: z.number().describe('The id of the output to update'),
  scheduleOutput: scheduleOutputSchema,
});

function errorResult(message: string) {
  return toolResult(JSON.stringify({status: 'error', message}));
}

export default function editScheduleOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof inputSchema, ScheduleCardScratchpadState>,
  ) {
    const {outputId, scheduleOutput} = params.input;
    const validatedScratchpad = validateAgentScratchpad<ScheduleCardScratchpadState>(
      params.toolUtils.agentScratchpad,
      NodeType.SCHEDULE,
    );
    const {card} = validatedScratchpad;

    const existingOutput = card.outputs.find((output) => output.id === outputId);

    if (!existingOutput) {
      return errorResult(`Schedule output with id ${outputId} not found.`);
    }

    if (existingOutput.type === OutputType.Fallback) {
      return errorResult('The Fallback output cannot be edited.');
    }

    const validationError = validateScheduleOutput(
      scheduleOutput,
      card.scheduleType as ScheduleType,
    );

    if (validationError) {
      return errorResult(validationError);
    }

    const target = existingOutput as ScheduleNodeOutputData;
    target.name = scheduleOutput.name ?? target.name;

    // Clear both fields first - they are mutually exclusive
    target.dateRange = undefined;
    target.daysOfWeek = undefined;

    if (scheduleOutput.dateRange) {
      target.dateRange = {
        start: DateTime.fromISO(scheduleOutput.dateRange.start, {
          zone: UTCTimezone,
          setZone: true,
        }).toUTC(),
        end: DateTime.fromISO(scheduleOutput.dateRange.end, {
          zone: UTCTimezone,
          setZone: true,
        }).toUTC(),
      };
    } else if (scheduleOutput.daysOfWeek?.length) {
      target.daysOfWeek = scheduleOutput.daysOfWeek.map((dow) => ({
        days: dow.days ?? [],
        start_minute: (dow.startHour ?? 0) * 60,
        end_minute: ((dow.endHour ?? 0) + 1) * 60,
      }));
    } else if (card.scheduleType === ScheduleType.DaysOfWeek) {
      target.daysOfWeek = [];
    }

    return toolResult('Schedule output updated');
  }

  return tool<typeof inputSchema, ScheduleCardScratchpadState>({
    name: V2CardAgentToolName.EditScheduleOutput,
    displayName: 'Editing schedule output',
    description: 'Edit a schedule output (validated against schedule type).',
    inputSchema,
    execute,
  });
}
